import React from "react";

/**
 * Flowing metallic-gold "thread" line-art echoing the book cover:
 * long sweeping organic curves in a gold gradient with a soft glow.
 * Decorative only — used behind framing areas, never over book pages.
 * (Export name kept as TopographicBackground for compatibility.)
 */
export const TopographicBackground = ({
  className = "",
  opacity = 0.5,
  animate = false,
  "data-testid": testId = "topographic-background",
}) => {
  const uid = React.useId().replace(/:/g, "");
  const gradId = `gold-thread-${uid}`;
  const glowId = `gold-glow-${uid}`;

  const lines = React.useMemo(() => {
    const paths = [];
    const count = 7;
    for (let i = 0; i < count; i++) {
      const base = 40 + i * 66;
      const amp = 40 + (i % 3) * 26;
      const dir = i % 2 === 0 ? 1 : -1;
      const d = `M -80 ${base}
        C 260 ${base - amp * dir}, 520 ${base + amp * dir}, 780 ${base - amp * 0.5 * dir}
        S 1180 ${base + amp * dir}, 1440 ${base - amp * 0.4 * dir}
        S 1780 ${base + amp * 0.8 * dir}, 1900 ${base - amp * 0.3 * dir}`;
      paths.push({ d, w: i % 3 === 0 ? 1.4 : 0.8 });
    }
    // a couple of looping accents like the cover's threads
    paths.push({
      d: `M 300 260 C 460 120, 720 140, 820 300 S 620 520, 500 420 S 200 340, 300 260`,
      w: 1,
    });
    paths.push({
      d: `M 1120 320 C 1260 200, 1480 220, 1540 360 S 1360 560, 1240 470 S 1000 400, 1120 320`,
      w: 1,
    });
    return paths;
  }, []);

  return (
    <svg
      data-testid={testId}
      className={className}
      viewBox="0 0 1800 520"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
      style={{ opacity }}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#D8B978" stopOpacity="0.15" />
          <stop offset="30%" stopColor="#E3CC98" stopOpacity="0.9" />
          <stop offset="55%" stopColor="#C5A059" stopOpacity="1" />
          <stop offset="80%" stopColor="#A9833F" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#C5A059" stopOpacity="0.15" />
        </linearGradient>
        <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g stroke={`url(#${gradId})`} fill="none" filter={`url(#${glowId})`} strokeLinecap="round">
        {lines.map((ln, i) => (
          <path
            key={i}
            d={ln.d}
            strokeWidth={ln.w}
            style={
              animate
                ? {
                    strokeDasharray: 4000,
                    strokeDashoffset: 4000,
                    animation: `draw-in 3.6s ${i * 0.16}s cubic-bezier(0.22,1,0.36,1) forwards`,
                  }
                : undefined
            }
          />
        ))}
      </g>
    </svg>
  );
};

/** Soft glowing gold orb — echoes the luminous "sun" on the book cover. */
export const GoldOrb = ({ className = "", size = 320, testId = "gold-orb" }) => (
  <div
    data-testid={testId}
    aria-hidden="true"
    className={`pointer-events-none absolute rounded-full ${className}`}
    style={{
      width: size,
      height: size,
      background:
        "radial-gradient(circle, rgba(245,225,170,0.85) 0%, rgba(225,204,152,0.45) 26%, rgba(197,160,89,0.20) 46%, rgba(197,160,89,0) 70%)",
      filter: "blur(2px)",
    }}
  />
);

/** Twinkling celestial stars (Andro-Pang-inspired luminous dreaminess). */
export const StarField = ({ count = 14, className = "", testId = "star-field" }) => {
  const stars = React.useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const seed = (i * 9301 + 49297) % 233280;
        const r = seed / 233280;
        const r2 = ((i * 4099 + 7907) % 233280) / 233280;
        const size = 3 + Math.round(r * 7);
        return {
          left: `${Math.round(r * 96) + 2}%`,
          top: `${Math.round(r2 * 92) + 2}%`,
          size,
          dur: `${3 + (i % 5)}s`,
          delay: `${(i % 7) * 0.4}s`,
        };
      }),
    [count]
  );
  return (
    <div
      data-testid={testId}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {stars.map((s, i) => (
        <span
          key={i}
          className="star"
          style={{ left: s.left, top: s.top, width: s.size, height: s.size, "--dur": s.dur, "--delay": s.delay }}
        />
      ))}
    </div>
  );
};

/**
 * Andro-Pang-inspired celestial topography: nested contour ridges rising
 * toward a luminous orb, with soft gradient fills — dreamy, minimal, surreal.
 */
export const CelestialTopography = ({ className = "", opacity = 0.7, testId = "celestial-topography" }) => {
  const uid = React.useId().replace(/:/g, "");
  const glow = `orb-${uid}`;
  const grad = `ridge-${uid}`;
  const ridges = React.useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const y = 150 + i * 26;
      const peak = 70 - i * 6;
      const d = `M -40 ${y}
        C 180 ${y - peak * 0.5}, 360 ${y - peak}, 500 ${y - peak}
        S 820 ${y - peak * 0.5}, 1040 ${y}`;
      arr.push({ d, o: 0.9 - i * 0.1 });
    }
    return arr;
  }, []);
  return (
    <svg
      data-testid={testId}
      className={className}
      viewBox="0 0 1000 260"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
      style={{ opacity }}
    >
      <defs>
        <radialGradient id={glow} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FBEEC6" stopOpacity="0.95" />
          <stop offset="35%" stopColor="#E7CC90" stopOpacity="0.55" />
          <stop offset="70%" stopColor="#C5A059" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#C5A059" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={grad} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#C5A059" stopOpacity="0.1" />
          <stop offset="50%" stopColor="#C5A059" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#C5A059" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="500" cy="120" r="150" fill={`url(#${glow})`} />
      <circle cx="500" cy="120" r="30" fill="#F3E1AE" opacity="0.9" />
      <g stroke={`url(#${grad})`} strokeWidth="1" fill="none">
        {ridges.map((r, i) => (
          <path key={i} d={r.d} opacity={r.o} />
        ))}
      </g>
    </svg>
  );
};

/**
 * TopoMap — a proper topographic contour map: several elevation "peaks",
 * each drawn as nested, organically-wobbling closed contour rings.
 */
export const TopoMap = ({ className = "", opacity = 0.5, seed = 1, testId = "topo-map" }) => {
  const uid = React.useId().replace(/:/g, "");
  const grad = `topo-${uid}`;
  const paths = React.useMemo(() => {
    const rnd = (() => {
      let s = seed * 9301 + 49297;
      return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
      };
    })();
    const centers = [
      { cx: 260, cy: 360, rings: 10, step: 33 },
      { cx: 740, cy: 600, rings: 12, step: 29 },
      { cx: 560, cy: 150, rings: 7, step: 38 },
      { cx: 120, cy: 760, rings: 6, step: 34 },
    ];
    const N = 72;
    const out = [];
    centers.forEach((c, ci) => {
      const phase = rnd() * Math.PI * 2;
      const wob = 0.05 + rnd() * 0.04;
      for (let ring = 1; ring <= c.rings; ring++) {
        const r = ring * c.step;
        let d = "";
        for (let i = 0; i <= N; i++) {
          const th = (2 * Math.PI * i) / N;
          const rad =
            r * (1 + wob * Math.sin(3 * th + phase + ci) + wob * 0.6 * Math.sin(5 * th + phase * 1.3));
          const x = c.cx + rad * Math.cos(th);
          const y = c.cy + rad * Math.sin(th);
          d += (i === 0 ? "M " : "L ") + x.toFixed(1) + " " + y.toFixed(1) + " ";
        }
        d += "Z";
        out.push({ d, o: Math.max(0.12, 0.85 - (ring / c.rings) * 0.62), w: ring === c.rings ? 1.1 : 0.7 });
      }
    });
    return out;
  }, [seed]);

  return (
    <svg
      data-testid={testId}
      className={className}
      viewBox="0 0 1000 1000"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
      style={{ opacity }}
    >
      <defs>
        <radialGradient id={grad} cx="50%" cy="45%" r="65%">
          <stop offset="0%" stopColor="#D9B978" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#C5A059" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#A9833F" stopOpacity="0.35" />
        </radialGradient>
      </defs>
      <g stroke={`url(#${grad})`} fill="none" strokeLinejoin="round">
        {paths.map((p, i) => (
          <path key={i} d={p.d} strokeWidth={p.w} opacity={p.o} />
        ))}
      </g>
    </svg>
  );
};

/** Fine paper-grain overlay for framing surfaces. */
export const PaperGrain = ({ className = "" }) => (
  <div
    aria-hidden="true"
    className={`paper-grain pointer-events-none absolute inset-0 opacity-[0.10] ${className}`}
  />
);
